/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { action, computed } from '@ember/object';
import { filter } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';
import ENV from '../../../../config/environment';
import moment from 'moment';

export default class LineItemController extends Controller {
  @service
  fetch;

  @service
  session;

  @service
  features;

  @service
  finance;

  @service
  paperToaster;

  @filter('model.transactions', function (txn) {
    switch (txn.type) {
      case TransactionType.TRANSFER:
        if (txn.displayAmount > 0) {
          return true;
        }
        break;
      case TransactionType.PAYMENT:
      case TransactionType.APPORTIONMENT:
        return true;
    }
    return false;
  })
  receipts;

  @filter('model.transactions', function (txn) {
    return txn.type === TransactionType.ADJUSTMENT;
  })
  adjustments;

  @filter('model.transactions', function (txn) {
    switch (txn.type) {
      case TransactionType.TRANSFER:
        if (txn.displayAmount < 0) {
          return true;
        }
        break;
      case TransactionType.DISCOUNT___EXEMPTION:
      case TransactionType.REFUND:
      case TransactionType.WRITEOFF:
      case TransactionType.VOID:
        return true;
    }
    return false;
  })
  otherCredits;

  @computed('model.transactions.@each.{type,amount}')
  get amountPaid() {
    return this.model.transactions
      .reduce((val, transaction) => {
        switch (transaction.type) {
          case TransactionType.PAYMENT:
          case TransactionType.TRANSFER:
          case TransactionType.REFUND:
          case TransactionType.APPORTIONMENT:
            return val + transaction.displayAmount;
          default:
            return val;
        }
      }, 0)
      .toFixed(2);
  }

  @computed('session.school.jurisdiction', 'model.item.isRefundable')
  get itemIsRefundable() {
    switch (this.get('session.school.jurisdiction')) {
      case Jurisdiction.AU_NSW:
        return this.get('model.item.isRefundable');
    }

    // By default, all items are refundable.
    return true;
  }

  @tracked
  refundType = 'direct';

  @tracked
  refundAmount = null;

  @action
  setRefundAmount(value) {
    this.refundAmount = value;
  }

  @tracked
  refundComment = '';

  @action
  setRefundComment(value) {
    this.refundComment = value;
  }

  @tracked
  refundDate = moment().format('YYYY-MM-DD');

  @action
  setRefundDate(value) {
    this.refundDate = value;
  }

  tillDisabled = false;

  @tracked
  editingTransaction = null;

  @tracked
  isEditingTransaction = false;

  PaymentMethod = PaymentMethod;

  TransactionType = TransactionType;

  @tracked
  errorMessage;

  @tracked
  refundDialogOpen;

  @tracked
  refundInProgress;

  @action
  toggleRefundDialog() {
    this.errorMessage = '';
    this.toggleProperty('refundDialogOpen');
    if (!this.refundDialogOpen) {
      this.resetRefundForm();
    }
    if (this.refundDialogOpen && this.features.sapFinance) {
      // Only refunds to PIA are available here; direct refunds can be made
      // via the "View all Payments" page.
      this.refundType = 'account';
    }
  }

  @action
  setRefundType(newType) {
    this.refundType = newType;
  }

  @action
  refund() {
    this.refundInProgress = true;
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/lineitems/${this.model.item.id}/:refund`;
    let data = {
      comment: this.refundComment,
      date: this.refundDate,
      amount: Math.round(this.refundAmount * 100),
    };
    if (this.refundType === 'account') {
      data.accountId = parseInt(this.get('model.item.account.id'), 10);
    } else {
      if (
        this.features.sapFinance &&
        !this.tillDisabled &&
        !isEmpty(this.get('finance.activeTill'))
      ) {
        data.tillId = parseInt(this.get('finance.activeTill.id'), 10);
        data.method = PaymentMethod.CASH;
      }
    }
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then(() => {
        this.paperToaster.show(`Line Item refunded successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.model.item.reload();
        this.model.transactions.update();
        this.refundDialogOpen = false;
        this.resetRefundForm();
      })
      .catch(() => {
        this.paperToaster.show('Error refunding Line Item!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
      })
      .then(() => {
        this.refundInProgress = false;
      });
  }

  @action
  editTransaction(transaction) {
    this.isEditingTransaction = true;
    this.editingTransaction = transaction;
  }

  @action
  closeEditTransaction() {
    this.isEditingTransaction = false;
    this.editingTransaction = null;
  }

  @action
  onTransactionDeleted() {
    this.model.transactions.update();
    this.model.item.reload();
  }

  resetRefundForm() {
    this.refundType = 'direct';
    this.refundAmount = null;
    this.refundComment = '';
    this.refundDate = moment().format('YYYY-MM-DD');
  }
}
